/**
 * Self-test suite for X3DH + Double Ratchet (Signal Protocol) primitives.
 *
 * Tests the crypto/session.ts + crypto/envelope.ts layer using
 * fully-in-memory StorageType stubs so no IndexedDB is required.
 *
 * Registered name: "X3DH + Double Ratchet (envelope + session)"
 *
 * Corresponds to item 1, Section A of Signal_standard_web.md.
 */

import {
  KeyHelper,
  SessionBuilder,
  SessionCipher,
  SignalProtocolAddress,
} from "@privacyresearch/libsignal-protocol-typescript";
import type {
  StorageType,
  KeyPairType,
} from "@privacyresearch/libsignal-protocol-typescript";
import type { DeviceType } from "@privacyresearch/libsignal-protocol-typescript/lib/session-types";
import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import { encodeEnvelope, decodeEnvelope } from "./envelope";

// ---------------------------------------------------------------------------
// In-memory StorageType stub
// ---------------------------------------------------------------------------

class MemorySignalStore implements StorageType {
  private identityKeyPair: KeyPairType | undefined;
  private registrationId: number | undefined;
  private preKeys = new Map<string, KeyPairType>();
  private signedPreKeys = new Map<string, KeyPairType>();
  private sessions = new Map<string, string>();
  private trustedIdentities = new Map<string, string>();

  async getIdentityKeyPair(): Promise<KeyPairType | undefined> {
    return this.identityKeyPair;
  }
  setIdentityKeyPair(kp: KeyPairType): void {
    this.identityKeyPair = kp;
  }

  async getLocalRegistrationId(): Promise<number | undefined> {
    return this.registrationId;
  }
  setRegistrationId(id: number): void {
    this.registrationId = id;
  }

  async isTrustedIdentity(
    identifier: string,
    identityKey: ArrayBuffer,
  ): Promise<boolean> {
    const stored = this.trustedIdentities.get(identifier);
    if (!stored) return true;
    return stored === ab2b64(identityKey);
  }

  async saveIdentity(
    encodedAddress: string,
    publicKey: ArrayBuffer,
  ): Promise<boolean> {
    const existing = this.trustedIdentities.get(encodedAddress);
    const newKey = ab2b64(publicKey);
    this.trustedIdentities.set(encodedAddress, newKey);
    return existing !== undefined && existing !== newKey;
  }

  async loadPreKey(keyId: string | number): Promise<KeyPairType | undefined> {
    return this.preKeys.get(String(keyId));
  }
  async storePreKey(keyId: string | number, kp: KeyPairType): Promise<void> {
    this.preKeys.set(String(keyId), kp);
  }
  async removePreKey(keyId: string | number): Promise<void> {
    this.preKeys.delete(String(keyId));
  }

  async loadSignedPreKey(
    keyId: string | number,
  ): Promise<KeyPairType | undefined> {
    return this.signedPreKeys.get(String(keyId));
  }
  async storeSignedPreKey(
    keyId: string | number,
    kp: KeyPairType,
  ): Promise<void> {
    this.signedPreKeys.set(String(keyId), kp);
  }
  async removeSignedPreKey(keyId: string | number): Promise<void> {
    this.signedPreKeys.delete(String(keyId));
  }

  async storeSession(address: string, record: string): Promise<void> {
    this.sessions.set(address, record);
  }
  async loadSession(address: string): Promise<string | undefined> {
    return this.sessions.get(address);
  }
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

function ab2b64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

function b642ab(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

/**
 * Build a fully-bootstrapped (regId + identityKey + signedPreKey + preKey)
 * in-memory store, plus the public DeviceType bundle suitable for passing
 * to SessionBuilder.processPreKey on the other side.
 */
async function buildParticipant(
  deviceId: number,
): Promise<{ store: MemorySignalStore; bundle: DeviceType; userId: string }> {
  const store = new MemorySignalStore();
  const userId = `user-${deviceId}-${Math.random().toString(36).slice(2)}`;

  const registrationId = KeyHelper.generateRegistrationId();
  const identityKeyPair = await KeyHelper.generateIdentityKeyPair();
  const signedPreKey = await KeyHelper.generateSignedPreKey(identityKeyPair, 1);
  const preKey = await KeyHelper.generatePreKey(1);

  store.setRegistrationId(registrationId);
  store.setIdentityKeyPair(identityKeyPair);
  await store.storeSignedPreKey(signedPreKey.keyId, signedPreKey.keyPair);
  await store.storePreKey(preKey.keyId, preKey.keyPair);

  const bundle: DeviceType = {
    identityKey: identityKeyPair.pubKey,
    signedPreKey: {
      keyId: signedPreKey.keyId,
      publicKey: signedPreKey.keyPair.pubKey,
      signature: signedPreKey.signature,
    },
    preKey: {
      keyId: preKey.keyId,
      publicKey: preKey.keyPair.pubKey,
    },
    registrationId,
  };

  return { store, bundle, userId };
}

/**
 * Alice processes Bob's bundle (X3DH initiator) and encrypts a message.
 * Returns the MessageType body bytes and kind as an envelope string.
 */
async function aliceEncrypt(
  aliceStore: MemorySignalStore,
  bobUserId: string,
  bobBundle: DeviceType,
  plaintext: string,
): Promise<string> {
  const address = new SignalProtocolAddress(bobUserId, 1);
  // Run X3DH only on the first call for this peer. Re-running processPreKey
  // would overwrite the live session, breaking the ratchet for subsequent
  // sends (and triggering Bad MAC on the receiver).
  if (!(await aliceStore.loadSession(address.toString()))) {
    const builder = new SessionBuilder(aliceStore, address);
    await builder.processPreKey(bobBundle);
  }

  const cipher = new SessionCipher(aliceStore, address);
  const msg = await cipher.encrypt(enc.encode(plaintext).buffer as ArrayBuffer);

  let bodyBytes: Uint8Array;
  if (!msg.body) {
    bodyBytes = new Uint8Array(0);
  } else if (typeof msg.body === "string") {
    bodyBytes = new Uint8Array(msg.body.length);
    for (let i = 0; i < msg.body.length; i++)
      bodyBytes[i] = msg.body.charCodeAt(i);
  } else {
    bodyBytes = new Uint8Array(msg.body as unknown as ArrayBuffer);
  }

  const kind = msg.type === 3 ? "pk" : "msg";
  return encodeEnvelope(kind as "pk" | "msg", 1, bodyBytes);
}

/**
 * Bob decrypts a single envelope from Alice.
 */
async function bobDecrypt(
  bobStore: MemorySignalStore,
  aliceUserId: string,
  envelope: string,
): Promise<string> {
  const decoded = decodeEnvelope(envelope);
  if (!decoded) throw new Error("bad envelope");
  const address = new SignalProtocolAddress(aliceUserId, decoded.senderDeviceId);
  const cipher = new SessionCipher(bobStore, address);

  let plainBuffer: ArrayBuffer;
  if (decoded.kind === "pk") {
    plainBuffer = await cipher.decryptPreKeyWhisperMessage(
      decoded.ciphertext.buffer as ArrayBuffer,
      "binary",
    );
  } else {
    plainBuffer = await cipher.decryptWhisperMessage(
      decoded.ciphertext.buffer as ArrayBuffer,
      "binary",
    );
  }
  return dec.decode(plainBuffer);
}

// ---------------------------------------------------------------------------
// Test cases
// ---------------------------------------------------------------------------

async function testRoundTrip(): Promise<TestCase> {
  const name = "alice→bob round-trip";
  try {
    const alice = await buildParticipant(1);
    const bob = await buildParticipant(2);

    const envelope = await aliceEncrypt(
      alice.store,
      bob.userId,
      bob.bundle,
      "hello kaether",
    );
    const plaintext = await bobDecrypt(bob.store, alice.userId, envelope);

    if (plaintext !== "hello kaether") {
      return fail(name, `expected "hello kaether", got "${plaintext}"`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testMultiMessageOutOfOrder(): Promise<TestCase> {
  const name = "multi-message out-of-order delivery";
  try {
    const alice = await buildParticipant(1);
    const bob = await buildParticipant(2);

    // Encrypt 3 messages in order
    const e1 = await aliceEncrypt(alice.store, bob.userId, bob.bundle, "msg1");
    const e2 = await aliceEncrypt(alice.store, bob.userId, bob.bundle, "msg2");
    const e3 = await aliceEncrypt(alice.store, bob.userId, bob.bundle, "msg3");

    // Deliver out of order: 3, 1, 2
    const p3 = await bobDecrypt(bob.store, alice.userId, e3);
    const p1 = await bobDecrypt(bob.store, alice.userId, e1);
    const p2 = await bobDecrypt(bob.store, alice.userId, e2);

    if (p1 !== "msg1" || p2 !== "msg2" || p3 !== "msg3") {
      return fail(
        name,
        `expected msg1/msg2/msg3, got "${p1}"/"${p2}"/"${p3}"`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testRatchetAdvancement(): Promise<TestCase> {
  const name = "ratchet chain-key advances between sends";
  try {
    const alice = await buildParticipant(1);
    const bob = await buildParticipant(2);

    const bobAddress = new SignalProtocolAddress(bob.userId, 1);

    // First message (prekey, establishes session)
    const e1 = await aliceEncrypt(alice.store, bob.userId, bob.bundle, "a");
    await bobDecrypt(bob.store, alice.userId, e1);

    // Snapshot Alice's session record before second send
    const sessionBefore = await alice.store.loadSession(
      bobAddress.toString(),
    );

    // Second message
    const e2 = await aliceEncrypt(alice.store, bob.userId, bob.bundle, "b");
    await bobDecrypt(bob.store, alice.userId, e2);

    // Snapshot after second send
    const sessionAfter = await alice.store.loadSession(
      bobAddress.toString(),
    );

    if (!sessionBefore || !sessionAfter) {
      return fail(name, "session record missing in store");
    }
    if (sessionBefore === sessionAfter) {
      return fail(
        name,
        "session record identical before and after second send — ratchet did not advance",
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testWrongSession(): Promise<TestCase> {
  const name = "wrong-session decrypt surfaces an error";
  try {
    const alice = await buildParticipant(1);
    const bob = await buildParticipant(2);
    const charlie = await buildParticipant(3);

    // Alice encrypts for Bob
    const envelope = await aliceEncrypt(
      alice.store,
      bob.userId,
      bob.bundle,
      "secret",
    );

    // Charlie (who has never spoken to Alice) tries to decrypt it
    let threw = false;
    let errorMessage = "";
    try {
      await bobDecrypt(charlie.store, alice.userId, envelope);
    } catch (e) {
      threw = true;
      errorMessage = e instanceof Error ? e.message : String(e);
    }

    if (!threw) {
      return fail(
        name,
        "expected decrypt with wrong session to throw, but it returned silently",
      );
    }
    return pass(name, `correctly threw: "${errorMessage}"`);
  } catch (e) {
    return fail(name, `setup threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testTamperedCiphertext(): Promise<TestCase> {
  const name = "tampered ciphertext produces an error";
  try {
    const alice = await buildParticipant(1);
    const bob = await buildParticipant(2);

    const envelope = await aliceEncrypt(
      alice.store,
      bob.userId,
      bob.bundle,
      "tamper me",
    );

    const decoded = decodeEnvelope(envelope);
    if (!decoded) return fail(name, "could not decode envelope for tampering");

    // Flip the last byte of the ciphertext (hits the GCM auth tag region)
    const tampered = new Uint8Array(decoded.ciphertext);
    tampered[tampered.length - 1] ^= 0xff;

    const tamperedEnvelope = encodeEnvelope(
      decoded.kind,
      decoded.senderDeviceId,
      tampered,
    );

    let threw = false;
    let errorMessage = "";
    try {
      await bobDecrypt(bob.store, alice.userId, tamperedEnvelope);
    } catch (e) {
      threw = true;
      errorMessage = e instanceof Error ? e.message : String(e);
    }

    if (!threw) {
      return fail(
        name,
        "expected tampered ciphertext to throw, but decrypt succeeded silently",
      );
    }
    return pass(name, `correctly threw: "${errorMessage}"`);
  } catch (e) {
    return fail(name, `setup threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testSignedPreKeySignatureRejection(): Promise<TestCase> {
  const name = "invalid signed-prekey signature is rejected";
  try {
    const alice = await buildParticipant(1);
    const bob = await buildParticipant(2);

    // Clone Bob's bundle but corrupt the signature
    const corruptedBundle: DeviceType = {
      ...bob.bundle,
      signedPreKey: {
        ...bob.bundle.signedPreKey,
        // Flip first byte of signature
        signature: (() => {
          const sig = new Uint8Array(
            bob.bundle.signedPreKey.signature as ArrayBuffer,
          ).slice();
          sig[0] ^= 0xff;
          return sig.buffer as ArrayBuffer;
        })(),
      },
    };

    let threw = false;
    let errorMessage = "";
    try {
      const address = new SignalProtocolAddress(bob.userId, 1);
      const builder = new SessionBuilder(alice.store, address);
      await builder.processPreKey(corruptedBundle);
    } catch (e) {
      threw = true;
      errorMessage = e instanceof Error ? e.message : String(e);
    }

    if (!threw) {
      return fail(
        name,
        "expected invalid signed-prekey signature to throw during processPreKey, but it was accepted",
      );
    }
    return pass(name, `correctly rejected with: "${errorMessage}"`);
  } catch (e) {
    return fail(name, `setup threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testCurve25519KeyLength(): Promise<TestCase> {
  const name = "Curve25519 identity public key is 32 bytes";
  try {
    const { bundle } = await buildParticipant(1);
    const pubKeyBytes = new Uint8Array(bundle.identityKey as ArrayBuffer);

    // libsignal stores a 33-byte "type || key" encoding (0x05 prefix).
    // The raw public key without the type byte is 32 bytes.
    // We test the actual wire length of the identity key buffer.
    // The library uses a 33-byte format (type byte + 32 key bytes).
    const rawLen = pubKeyBytes.length;
    if (rawLen !== 33 && rawLen !== 32) {
      return fail(
        name,
        `expected 32 or 33 bytes (with optional type byte), got ${rawLen}`,
      );
    }
    // Strip the type byte if present (0x05 = DJB_TYPE)
    const keyBytes = rawLen === 33 ? pubKeyBytes.slice(1) : pubKeyBytes;
    if (keyBytes.length !== 32) {
      return fail(
        name,
        `expected 32 raw key bytes, got ${keyBytes.length}`,
      );
    }
    return pass(name, `length = ${rawLen} bytes (${rawLen === 33 ? "with type prefix" : "raw"})`);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testEnvelopeRoundTrip(): Promise<TestCase> {
  const name = "envelope encode→decode round-trip preserves all fields";
  try {
    const payload = new Uint8Array([1, 2, 3, 4, 255, 0, 128]);
    const encoded = encodeEnvelope("pk", 42, payload);
    const decoded = decodeEnvelope(encoded);

    if (!decoded) return fail(name, "decodeEnvelope returned null");
    if (decoded.kind !== "pk")
      return fail(name, `kind: expected "pk", got "${decoded.kind}"`);
    if (decoded.senderDeviceId !== 42)
      return fail(
        name,
        `senderDeviceId: expected 42, got ${decoded.senderDeviceId}`,
      );
    if (decoded.ciphertext.length !== payload.length)
      return fail(
        name,
        `ciphertext length: expected ${payload.length}, got ${decoded.ciphertext.length}`,
      );
    for (let i = 0; i < payload.length; i++) {
      if (decoded.ciphertext[i] !== payload[i]) {
        return fail(name, `byte ${i} mismatch: ${decoded.ciphertext[i]} !== ${payload[i]}`);
      }
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testNonE2EEEnvelopeReturnsFalse(): Promise<TestCase> {
  const name = "non-e2ee plaintext is not identified as an envelope";
  try {
    const decoded = decodeEnvelope("Hello, this is a plain message");
    if (decoded !== null) {
      return fail(
        name,
        "expected decodeEnvelope to return null for plain text, got a result",
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testSessionEstablishedWithoutOneTimePreKey(): Promise<TestCase> {
  const name = "session establishes without one-time prekey (OPK omitted)";
  try {
    const alice = await buildParticipant(1);
    const bob = await buildParticipant(2);

    // Bundle without OPK
    const bundleNoOpk: DeviceType = {
      identityKey: bob.bundle.identityKey,
      signedPreKey: bob.bundle.signedPreKey,
      registrationId: bob.bundle.registrationId,
      // preKey omitted intentionally
    };

    const envelope = await aliceEncrypt(
      alice.store,
      bob.userId,
      bundleNoOpk,
      "no opk test",
    );
    const plaintext = await bobDecrypt(bob.store, alice.userId, envelope);

    if (plaintext !== "no opk test") {
      return fail(name, `expected "no opk test", got "${plaintext}"`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "X3DH + Double Ratchet (envelope + session)",
  isVerified: true,
  run: async () => {
    const cases: TestCase[] = [
      await testRoundTrip(),
      await testMultiMessageOutOfOrder(),
      await testRatchetAdvancement(),
      await testWrongSession(),
      await testTamperedCiphertext(),
      await testSignedPreKeySignatureRejection(),
      await testCurve25519KeyLength(),
      await testEnvelopeRoundTrip(),
      await testNonE2EEEnvelopeReturnsFalse(),
      await testSessionEstablishedWithoutOneTimePreKey(),
    ];
    return buildSuiteResult(cases);
  },
});
